import { useState, useEffect } from 'react'

const PLATFORM_HOSTS_EXACT = new Set([
  'localhost', '127.0.0.1',
  'app.agntx.com.au', 'agntx.com.au',
  'demo.agntx.agency',
])
const PLATFORM_REDIRECT = 'https://agntx.com.au'

export const DEMO_DOMAIN_BASE = 'demo.agntx.agency'

function isCustomHost(hostname: string): boolean {
  return !PLATFORM_HOSTS_EXACT.has(hostname)
}

/**
 * Platform-managed subdomains (<slug>.agntx.com.au, the free/default tier
 * before an agency sets up their own custom domain) only ever get ONE
 * domain row, unlike custom-domain agencies who get a dedicated subdomain
 * per purpose (inspections.example.com, listings.example.com, etc.) —
 * there's no DNS/cert setup for a second-level subdomain like
 * inspections.<slug>.agntx.com.au. Instead the same domain switches
 * between the main app and the dedicated inspections portal shell via
 * ?portal=inspections|app, persisted in sessionStorage (keyed by hostname)
 * since the two shells have entirely separate route trees — a plain path
 * prefix isn't enough once you navigate around inside either one.
 */
const PORTAL_PURPOSE_KEY_PREFIX = 'agntx_portal_purpose:'

function resolvePlatformSubdomainPurpose(hostname: string): string {
  const params = new URLSearchParams(window.location.search)
  const requested = params.get('portal')
  const storageKey = PORTAL_PURPOSE_KEY_PREFIX + hostname

  if (requested === 'inspections' || requested === 'app') {
    try { sessionStorage.setItem(storageKey, requested) } catch { /* ignore */ }
    return requested
  }

  try {
    const stored = sessionStorage.getItem(storageKey)
    if (stored === 'inspections' || stored === 'app') return stored
  } catch { /* ignore */ }

  return 'app'
}

async function resolveHostToAccount(hostname: string): Promise<{ accountId: string | null; purpose: string | null }> {
  const { restRpc } = await import('@/lib/supabaseRest')

  if (hostname.endsWith('.agntx.com.au')) {
    const slug = hostname.split('.')[0]
    const data = await restRpc<string>('get_account_id_by_slug', { p_slug: slug })
    return { accountId: data ?? null, purpose: resolvePlatformSubdomainPurpose(hostname) }
  }

  if (hostname.endsWith('.' + DEMO_DOMAIN_BASE)) {
    const slug = hostname.split('.')[0]
    const data = await restRpc<string>('get_account_id_by_slug', { p_slug: slug })
    return { accountId: data ?? null, purpose: resolvePlatformSubdomainPurpose(hostname) }
  }

  const data = await restRpc<{ account_id: string; purpose: string }[]>('get_domain_info_for_host', { p_domain: hostname })
  const row = data?.[0] ?? null
  return { accountId: row?.account_id ?? null, purpose: row?.purpose ?? null }
}

export function useHostResolver() {
  const [resolvedAccountId, setResolvedAccountId] = useState<string | null>(null)
  const [resolvedPurpose,   setResolvedPurpose]   = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const hostname = window.location.hostname

    if (!isCustomHost(hostname)) {
      setLoading(false)
      return
    }

    /* Dev bypass — for *.local hostnames, derive purpose from the subdomain
       prefix and account from VITE_DEV_ACCOUNT_ID.
       Add to .env.local:  VITE_DEV_ACCOUNT_ID=<account-uuid>
       Then use:
         http://inspections.local:5173/   → purpose: inspections
         http://listings.local:5173/      → purpose: listings
         http://bookings.local:5173/      → purpose: bookings   */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any).env as Record<string, string> | undefined
    if (hostname.endsWith('.local') && env?.VITE_DEV_ACCOUNT_ID) {
      const prefix = hostname.replace(/\.local$/, '')
      setResolvedAccountId(env.VITE_DEV_ACCOUNT_ID)
      setResolvedPurpose(prefix)
      setLoading(false)
      return
    }

    void (async () => {
      const { accountId, purpose } = await resolveHostToAccount(hostname)

      if (accountId) {
        setResolvedAccountId(accountId)
        setResolvedPurpose(purpose)
        setLoading(false)
      } else {
        window.location.replace(PLATFORM_REDIRECT)
      }
    })()
  }, [])

  return { resolvedAccountId, resolvedPurpose, loading }
}

export function isDemoDomainHost(): boolean {
  const hostname = window.location.hostname
  return hostname.endsWith('.' + DEMO_DOMAIN_BASE)
}
